/**
 * «Активность → Контроль» (`?view=control`): аномалии смены по сотрудникам — пропущенные, которые никто
 * не взял, и пустая линия. Пишет их фон `activity_control`, экран только читает и ставит отметки разбора
 * (спросили, ответ, закрыть). Вопрос сотруднику отправляют отдельно — экран даёт текст и «Копировать».
 * Спеки `docs/specs/2026-11-03-activity-control-design.md`, `docs/specs/2026-11-03-activity-control-ux-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Tile } from "@/components/activity/DaysView";
import { IdleNotifyDialog } from "@/components/activity/IdleNotifyDialog";
import { Combo } from "@/components/Combo";
import {
  type ControlAction,
  type ControlIssue,
  type ControlKind,
  type ControlList,
  type ControlStatus,
  loadControl,
  markControl,
  resendIdleNotify,
} from "@/lib/activity";
import { ACTIVITY_IDLE_NOTIFY, useCan } from "@/lib/access";

export type StatusFilter = "unresolved" | "all" | ControlStatus;

export const KINDS: { id: ControlKind; label: string; hint: string; tone: string }[] = [
  {
    id: "line_empty",
    label: "Линия пуста",
    hint: "в смену 10–21 на линии никого не было от 10 минут",
    tone: "bg-err-bg text-err",
  },
  {
    id: "unreached",
    label: "Не дошло",
    hint: "вызов никто не взял: статус на линии и свободен, очередь отдавала вызов от 10 с, но телефон не в сети и Bitrix в браузере не ответил",
    tone: "bg-warn-bg text-warn",
  },
  {
    id: "not_answered",
    label: "Не взял",
    hint: "вызов никто не взял: дошёл до свободного сотрудника, трубку не взяли",
    tone: "bg-warn-bg text-warn",
  },
  {
    id: "idle",
    label: "Бездействие",
    hint: "статус на линии, а от 30 минут нет ни одного следа работы: звонка, сообщения, правки сделки или лида, действия в «Чатах» и в панели расширения",
    tone: "bg-err-bg text-err",
  },
  {
    id: "left_early",
    label: "Ушёл раньше",
    hint: "статус «Ушел» раньше конца графика от 15 минут, и человек не вернулся",
    tone: "bg-neutral-bg text-fg-muted",
  },
  {
    id: "no_show",
    label: "Не вышел",
    hint: "по графику смена, через 30 минут после начала выхода на линию нет; замены в графике не отмечены",
    tone: "bg-neutral-bg text-fg-muted",
  },
];
const KIND = Object.fromEntries(KINDS.map((k) => [k.id, k])) as Record<ControlKind, (typeof KINDS)[number]>;
/** Вид, которого этот фронт ещё не знает (фон новее сборки), — не падать, а показать код. */
const kindOf = (id: string) => KIND[id as ControlKind] ?? { id, label: id, hint: "", tone: "bg-neutral-bg text-fg-muted" };

const STATUSES: { id: StatusFilter; label: string }[] = [
  { id: "unresolved", label: "неразобранные" },
  { id: "all", label: "все" },
  { id: "new", label: "новые" },
  { id: "asked", label: "спросили" },
  { id: "answered", label: "ответили" },
  { id: "closed", label: "закрыто" },
];
const STATUS_LABEL: Record<ControlStatus, string> = {
  new: "Новая",
  asked: "Спросили",
  answered: "Ответили",
  closed: "Закрыто",
};
const STATUS_TONE: Record<ControlStatus, string> = {
  new: "bg-warn-bg text-warn",
  asked: "bg-neutral-bg text-fg",
  answered: "bg-ok-bg text-ok",
  closed: "bg-neutral-bg text-fg-faint",
};

const unresolved = (i: ControlIssue) => i.status === "new" || i.status === "asked";
const matches = (i: ControlIssue, s: StatusFilter) =>
  s === "all" ? true : s === "unresolved" ? unresolved(i) : i.status === s;

const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;
const weekday = (day: string) => WEEKDAY[new Date(`${day}T12:00:00`).getDay()];
const hm = (iso: string) => iso.slice(11, 16);
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Какой список «По часам» открыть у дня аномалии: тот же вид, что в журнале звонков. */
const HOURS_LIST: Partial<Record<ControlKind, string>> = {
  line_empty: "nobody",
  unreached: "unreached",
  not_answered: "ask",
  idle: "people",
};

function hoursHref(i: ControlIssue): string {
  const q = new URLSearchParams({ from: i.day, view: "hours" });
  const list = HOURS_LIST[i.kind];
  if (list) {
    q.set("hour", String(Number(i.from.slice(11, 13))));
    q.set("list", list);
  }
  return `/activity?${q}`;
}

function Span({ i }: { i: ControlIssue }) {
  if (i.kind === "left_early") return <>«Ушел» в {i.facts.left_at ? hm(i.facts.left_at) : hm(i.from)}</>;
  if (i.kind === "no_show") return <>{i.facts.plan ?? "—"}</>;
  if (i.kind === "idle" && i.facts.segments?.length)
    return (
      <span className="flex flex-col">
        {i.facts.segments.map((s) => (
          <Link
            key={s.from}
            to={`/activity/${i.user_id}?date=${i.day}&at=${s.from}-${s.to}`}
            className="text-accent-text hover:underline"
            title="смена сотрудника: что было до, во время и после этого отрезка"
          >
            {s.from}–{s.to} <span className="text-fg-faint">{s.minutes} мин</span>
          </Link>
        ))}
      </span>
    );
  const a = hm(i.from);
  const b = hm(i.to);
  return <>{a === b ? a : `${a}–${b}`}</>;
}

function Calls({ i }: { i: ControlIssue }) {
  const ids = i.facts.calls ?? [];
  if (!ids.length) return <span className="text-fg-faint">—</span>;
  return (
    <span className="flex flex-wrap gap-x-2 gap-y-0.5">
      {ids.map((id, n) => (
        <Link key={id} to={`/calls/${id}`} className="text-accent-text hover:underline" title="карточка звонка: запись и журнал">
          {i.facts.times?.[n] ?? `вызов ${n + 1}`}
        </Link>
      ))}
    </span>
  );
}

/** «Сообщений N · принято M» за отрезок: красным, когда переписка шла, а звонков не приняли. */
function Busy({ i }: { i: ControlIssue }) {
  const f = i.facts;
  if (i.kind === "line_empty")
    return (
      <span className={f.answered ? "" : "font-medium text-err"}>
        входящих {f.incoming ?? i.count} · принято {f.answered ?? 0}
      </span>
    );
  if (i.kind === "idle")
    return (
      <span>
        <span className="font-medium text-err">без следов {f.minutes ?? 0} мин</span>
        {f.plan === null && <span className="text-fg-muted"> · вне графика</span>}
      </span>
    );
  if (f.messages === undefined) return <span className="text-fg-faint">график {f.plan ?? "—"}</span>;
  const тревога = (f.messages ?? 0) > 0 && !f.accepted;
  return (
    <span title={`за день сообщений ${f.messages_day ?? "—"}`}>
      <span className={тревога ? "font-medium text-err" : ""}>
        сообщений {f.messages} · принято {f.accepted ?? 0}
      </span>
      {f.plan === null && <span className="text-fg-muted"> · вне графика</span>}
    </span>
  );
}

function Badge({ tone, children, title }: { tone: string; children: React.ReactNode; title?: string }) {
  return (
    <span className={`inline-block rounded-[var(--radius-ui)] px-1.5 py-0.5 text-[11.5px] leading-tight ${tone}`} title={title}>
      {children}
    </span>
  );
}

function Details({ i, onSaved }: { i: ControlIssue; onSaved: (x: ControlIssue) => void }) {
  const [answer, setAnswer] = useState(i.answer ?? "");
  const [copied, setCopied] = useState("");
  const mark = useMutation({
    mutationFn: ({ action, text }: { action: ControlAction; text?: string }) => markControl(i.id, action, text),
    onSuccess: onSaved,
  });
  const dirty = answer.trim() !== (i.answer ?? "").trim();
  const canNotify = useCan(ACTIVITY_IDLE_NOTIFY) && i.kind === "idle";
  const [notifying, setNotifying] = useState(false);
  const qc = useQueryClient();
  const resend = useMutation({
    mutationFn: () => resendIdleNotify(i.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["activity-control"] }),
  });
  const copy = () =>
    navigator.clipboard.writeText(i.question).then(
      () => setCopied("Вопрос скопирован. Отправьте его отдельно, затем отметьте «Спросили»"),
      () => setCopied("Не удалось скопировать: выделите текст и нажмите Ctrl+C"),
    );
  const btn =
    "h-7 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12px] text-fg hover:bg-bg-hover disabled:opacity-40";
  return (
    <div className="flex flex-col gap-3 px-3 pb-3 pl-10 text-[12.5px]">
      <p className="max-w-[900px] text-fg">{i.text}</p>
      {i.kind === "line_empty" && i.facts.who?.length ? (
        <p className="text-fg-muted">Где были люди: {i.facts.who.join("; ")}</p>
      ) : null}
      <div className="flex max-w-[900px] flex-col gap-1.5 rounded-[var(--radius-ui)] border border-border bg-bg p-2.5">
        <div className="text-[12px] font-medium text-fg-muted">Вопрос {i.user_id ? "сотруднику" : "смене"}</div>
        <p className="whitespace-pre-wrap text-fg">{i.question}</p>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={btn} onClick={copy}>
            Копировать
          </button>
          <span className="text-[12px] text-fg-faint" aria-live="polite">
            {copied || "Отправьте отдельно, затем отметьте «Спросили»"}
          </span>
        </div>
      </div>
      {i.asked_date && (
        <div className="text-fg-muted">
          Спросили {dm(i.asked_date.slice(0, 10))} в {hm(i.asked_date)}
        </div>
      )}
      <label className="flex max-w-[900px] flex-col gap-1">
        <span className="text-[12px] text-fg-muted">Ответ</span>
        <textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          rows={2}
          placeholder="Что ответил сотрудник"
          className="rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1.5 text-[12.5px] text-fg outline-none focus:border-accent"
        />
      </label>
      {canNotify && (
        <div className="flex flex-wrap items-center gap-2">
          {!i.notify ? (
            <button
              type="button"
              className={`${btn} border-accent`}
              title="Поставить в CRM «Ушел» на отрезки без следов и написать сотруднику с вашего личного Telegram — сначала предпросмотр"
              onClick={() => setNotifying(true)}
            >
              Уведомить
            </button>
          ) : i.notify === "sent" ? (
            <span className="text-ok">Уведомили: статусы сменены, сообщение отправлено</span>
          ) : (
            <>
              <span className="text-warn">
                Статусы сменены, сообщение {i.notify === "send_failed" ? "не ушло" : "не отправляли: Telegram сотрудника не найден"}
              </span>
              {i.notify === "send_failed" && (
                <button type="button" className={btn} disabled={resend.isPending} onClick={() => resend.mutate()}>
                  {resend.isPending ? "Отправляю…" : "Отправить ещё раз"}
                </button>
              )}
              {resend.isError && <span className="text-err">{String(resend.error.message)}</span>}
            </>
          )}
        </div>
      )}
      {notifying && <IdleNotifyDialog id={i.id} onClose={() => setNotifying(false)} />}
      <div className="flex flex-wrap items-center gap-2">
        {i.status === "new" && (
          <button type="button" className={btn} disabled={mark.isPending} onClick={() => mark.mutate({ action: "asked" })}>
            Спросили
          </button>
        )}
        <button
          type="button"
          className={`${btn} ${dirty && answer.trim() ? "border-accent" : ""}`}
          disabled={mark.isPending || !dirty || !answer.trim()}
          onClick={() => mark.mutate({ action: "answer", text: answer })}
        >
          Сохранить ответ
        </button>
        {dirty && (
          <button type="button" className={btn} disabled={mark.isPending} onClick={() => setAnswer(i.answer ?? "")}>
            Отмена
          </button>
        )}
        {i.status === "closed" ? (
          <button type="button" className={btn} disabled={mark.isPending} onClick={() => mark.mutate({ action: "reopen" })}>
            Вернуть в работу
          </button>
        ) : (
          <button
            type="button"
            className={btn}
            disabled={mark.isPending || dirty}
            title={dirty ? "Сохраните ответ или нажмите «Отмена»" : undefined}
            onClick={() => mark.mutate({ action: "close" })}
          >
            Закрыть
          </button>
        )}
        <span className="text-[12px]" aria-live="polite">
          {mark.isPending ? (
            <span className="text-fg-muted">Сохраняем…</span>
          ) : mark.isError ? (
            <span className="text-err" role="alert">
              Не удалось сохранить: {String(mark.error.message)}
            </span>
          ) : mark.isSuccess ? (
            <span className="text-fg-faint">Сохранено</span>
          ) : null}
        </span>
      </div>
    </div>
  );
}

function Row({ i, open, onToggle, onSaved }: { i: ControlIssue; open: boolean; onToggle: () => void; onSaved: (x: ControlIssue) => void }) {
  const k = kindOf(i.kind);
  const идёт = i.day === today() && i.status === "new";
  return (
    <div className="border-t border-border first:border-t-0">
      <div className="grid grid-cols-[28px_64px_110px_136px_minmax(0,1fr)_minmax(0,220px)_96px] items-start gap-x-3 px-3 py-2 text-[12.5px] max-md:grid-cols-[28px_1fr_auto] max-md:gap-y-1">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={open ? "Свернуть" : "Раскрыть"}
          className="flex size-6 items-center justify-center rounded-[var(--radius-ui)] border border-border text-fg-muted hover:bg-bg-hover"
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="max-md:col-start-2">
          <Link to={hoursHref(i)} className="text-accent-text hover:underline" title="этот день «По часам»">
            {dm(i.day)}
          </Link>{" "}
          <span className="text-fg-faint">{weekday(i.day)}</span>
        </div>
        <div className="max-md:col-start-2">
          <Badge tone={k.tone} title={k.hint}>
            {k.label}
          </Badge>
        </div>
        <div className="tabular-nums max-md:col-start-2">
          <Span i={i} />
          {идёт && <div className="text-[11.5px] text-fg-faint">сегодня, ещё обновляется</div>}
        </div>
        <div className="min-w-0 max-md:col-start-2">
          {i.facts.calls?.length ? (
            <>
              <span className="text-fg-muted">
                {i.kind === "idle" ? "пропущено" : "вызовов"} {i.facts.calls.length}:{" "}
              </span>
              <Calls i={i} />
            </>
          ) : (
            <span className="text-fg-faint">—</span>
          )}
        </div>
        <div className="max-md:col-start-2">
          <Busy i={i} />
        </div>
        <div className="text-right max-md:col-start-3 max-md:row-start-1">
          <Badge tone={STATUS_TONE[i.status]}>{STATUS_LABEL[i.status]}</Badge>
        </div>
      </div>
      {open && <Details i={i} onSaved={onSaved} />}
    </div>
  );
}

export function ControlView({
  from,
  to,
  status,
  kind,
  user,
  onFilter,
}: {
  from: string;
  to: string;
  status: StatusFilter;
  kind: ControlKind | "";
  user: string;
  onFilter: (next: { status?: StatusFilter; kind?: ControlKind | ""; user?: string }) => void;
}) {
  const key = ["activity-control", from, to];
  const qc = useQueryClient();
  const q = useQuery({ queryKey: key, queryFn: () => loadControl(from, to) });
  const [open, setOpen] = useState<number | null>(null);

  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Загружаю аномалии…</div>;
  if (q.isError)
    return (
      <div className="flex items-center gap-3 text-[12.5px] text-err">
        Не удалось загрузить аномалии: {String(q.error.message)}
        <button type="button" onClick={() => q.refetch()} className="h-7 rounded-[var(--radius-ui)] border border-border px-2.5 text-fg hover:bg-bg-hover">
          Повторить
        </button>
      </div>
    );

  const saved = (x: ControlIssue) =>
    qc.setQueryData<ControlList>(key, (old) =>
      old ? { ...old, items: old.items.map((i) => (i.id === x.id ? { ...i, ...x, name: i.name } : i)) } : old,
    );

  const all = q.data.items;
  const people = new Map<string, string>();
  for (const i of all) if (i.user_id) people.set(String(i.user_id), i.name);
  const byUser = all.filter((i) => !user || (user === "shift" ? i.user_id === null : String(i.user_id) === user));
  const shown = byUser.filter((i) => matches(i, status) && (!kind || i.kind === kind));

  const groups = new Map<string, { title: string; sub: string; items: ControlIssue[] }>();
  for (const i of shown) {
    const g = i.user_id === null ? "shift" : String(i.user_id);
    if (!groups.has(g))
      groups.set(g, i.user_id === null ? { title: "Смена", sub: "на линии никого", items: [] } : { title: i.name || `#${i.user_id}`, sub: "Контакт Центр", items: [] });
    groups.get(g)!.items.push(i);
  }
  const ordered = [...groups.entries()]
    .sort(([a, x], [b, y]) => (a === "shift" ? -1 : b === "shift" ? 1 : x.title.localeCompare(y.title, "ru")))
    .map(([id, g]) => ({ id, ...g, items: g.items.sort((a, b) => b.from.localeCompare(a.from)) }));

  const seg = (on: boolean) =>
    `h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12px] ${on ? "border-fg bg-bg text-fg" : "border-border text-fg-muted hover:bg-bg-hover"}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7">
        <button
          type="button"
          className={`rounded-[var(--radius-card)] text-left ${status === "unresolved" && !kind ? "outline-2 outline-accent" : ""}`}
          onClick={() => onFilter({ status: "unresolved", kind: "" })}
          aria-pressed={status === "unresolved" && !kind}
        >
          <Tile label="Неразобранные" value={byUser.filter(unresolved).length} hint="новые и спросили" />
        </button>
        {KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            className={`rounded-[var(--radius-card)] text-left ${kind === k.id ? "outline-2 outline-accent" : ""}`}
            onClick={() => onFilter({ kind: kind === k.id ? "" : k.id })}
            aria-pressed={kind === k.id}
            title={k.hint}
          >
            <Tile
              label={k.label}
              value={byUser.filter((i) => i.kind === k.id).length}
              hint={`неразобранных ${byUser.filter((i) => i.kind === k.id && unresolved(i)).length}`}
            />
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted">
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Статус">
          {STATUSES.map((s) => (
            <button key={s.id} type="button" onClick={() => onFilter({ status: s.id })} aria-pressed={status === s.id} className={seg(status === s.id)}>
              {s.label}
            </button>
          ))}
        </div>
        <Combo
          label="Сотрудник"
          value={user}
          options={[
            { value: "", label: "все" },
            { value: "shift", label: "Смена (пустая линия)" },
            ...[...people.entries()].sort((a, b) => a[1].localeCompare(b[1], "ru")).map(([value, label]) => ({ value, label })),
          ]}
          onChange={(u) => onFilter({ user: u })}
          placeholder="имя сотрудника"
          width={240}
        />
        {kind && (
          <button type="button" className={seg(true)} onClick={() => onFilter({ kind: "" })}>
            вид: {kindOf(kind).label} ✕
          </button>
        )}
      </div>

      {!all.length ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-[13px] text-fg-muted">
          Аномалий за период нет: пропущенных, которые никто не взял, и пустой линии не было.
        </div>
      ) : !ordered.length ? (
        <div className="flex items-center gap-3 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-[13px] text-fg-muted">
          Ничего не найдено по фильтрам.
          <button type="button" className={seg(false)} onClick={() => onFilter({ status: "all", kind: "", user: "" })}>
            Сбросить фильтры
          </button>
        </div>
      ) : (
        ordered.map((g) => (
          <section key={g.id} className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot={`контроль: ${g.title}`}>
            <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border px-3 py-2">
              <div>
                <span className="text-[13px] font-medium text-fg">{g.title}</span>{" "}
                <span className="text-[12px] text-fg-faint">{g.sub}</span>
              </div>
              <div className="text-[12px] text-fg-muted">
                аномалий {g.items.length} · неразобранных {g.items.filter(unresolved).length}
              </div>
            </header>
            {g.items.map((i) => (
              <Row key={i.id} i={i} open={open === i.id} onToggle={() => setOpen(open === i.id ? null : i.id)} onSaved={saved} />
            ))}
          </section>
        ))
      )}
      <div className="text-[12px] text-fg-faint">
        Аномалия — вызов, который никто не взял, пустая линия в смену 10–21 или бездействие: статус на линии, а
        от 30 минут нет ни одного следа работы. Вызов, взятый коллегой, сюда не попадает. Фон пересчитывает сегодня и вчера раз в 10 минут: сегодняшняя новая аномалия может вырасти или
        исчезнуть. Лог показывает, что вызов не дошёл, но не почему — причину спрашиваем у человека.
      </div>
    </div>
  );
}
